/**
 * ROLES, AND WHAT CHANGING ONE WOULD ACTUALLY DO.
 *
 * A role is held by people, so a permission taken off it is a permission taken
 * off everybody holding it — and that is discovered, normally, by the person
 * who can no longer do his job. This screen shows it BEFORE the change: pick
 * the permissions to add or remove and it names the holders who gain or lose
 * something, and says of each whether they keep it anyway because they hold it
 * directly or through another role.
 *
 * The permission list is the one the server sent with the session, so a manager
 * cannot type a permission into existence, and he cannot give away a permission
 * he does not hold himself — the door refuses that, and the screen does not
 * offer it (M2-25).
 */
import * as React from 'react';
import { useState } from 'react';
import { api, can, type Session } from '../api.js';
import { useAsk, useRun, go } from '../hooks.js';
import { Banner, Button, Card, Loading, Pill, Register, Text, TextArea, type Column } from '../ui.js';
import { Page } from '../shell.js';
import { count, words } from '../format.js';
import { screenById } from '../../../src/screens/contracts.js';

const MESSAGES = screenById('roles')!.messages;

export function Roles({ id, session }: { id: string | null; session: Session }) {
  if (id === 'new') return <NewRole session={session} />;
  if (id) return <OneRole roleId={Number(id)} session={session} />;
  return <RoleRegister session={session} />;
}

/**
 * The first sentence of a description, for a table cell. A role's full ruling is worth
 * having and worth reading — on the role's page, not folded into a row six lines deep.
 */
function firstSentence(text: string): string {
  const t = text.trim();
  const stop = t.search(/[.:]\s/);
  const cut = stop > 20 ? t.slice(0, stop + 1) : t;
  return cut.length > 120 ? `${cut.slice(0, 117).trimEnd()}…` : cut;
}

function RoleRegister({ session }: { session: Session }) {
  const asked = useAsk(() => api.view('roles.list', {}), [], MESSAGES);
  const columns: readonly Column<{ roleId: number; code: string; name: string; description: string | null; active: boolean; functionCount: number; holderCount: number }>[] = [
    { key: 'code', head: 'Code', cell: (r) => <strong>{r.code}</strong> },
    // 14 Oct — ONE LINE IN THE LIST. The branch manager's description runs to six lines of
    // ruling and filled the whole row; the full text belongs on the role's own page, where
    // somebody has gone to read it. Here it is the first sentence and no more.
    { key: 'name', head: 'Name', cell: (r) => (
      <>{r.name}
        {r.description
          ? <div className="muted one-line" title={r.description}>{firstSentence(r.description)}</div>
          : null}</>) },
    { key: 'fns', head: 'Permissions', right: true, cell: (r) => count(r.functionCount) },
    { key: 'holders', head: 'People holding it', right: true, cell: (r) => count(r.holderCount) },
    { key: 'active', head: 'Status', cell: (r) => <Pill tone={r.active ? 'good' : 'muted'}>{r.active ? 'in use' : 'closed'}</Pill> },
  ];
  return (
    <Page title="Roles" sub="A role is a bundle of permissions, and everybody holding it gets every one of them."
          actions={<>
            <Button onClick={() => go('#/users')}>Users</Button>
            {can(session, 'role.manage') ? <Button kind="primary" onClick={() => go('#/roles/new')}>New role</Button> : null}
          </>}>
      <Card>
        {asked.loading ? <Loading what="roles" /> : asked.error ? <Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} />
          : <Register columns={columns} rows={asked.data!.rows} empty="This company has no roles — every permission is granted to people directly."
                      onOpen={(r) => go(`#/roles/${r.roleId}`)} />}
      </Card>
    </Page>
  );
}

/** What the signed-in person may hand out: he cannot give away what he does not hold (M2-25). */
function grantable(session: Session): string[] {
  return [...session.functions].sort();
}

/**
 * WHAT A PERMISSION LETS YOU DO (audit G1, view `functions.list`, 0079).
 *
 * The catalogue is read once per screen and looked up by code. A permission
 * the catalogue does not carry is shown by its code alone rather than hidden
 * or guessed at: the list a manager may grant comes from his session, and a
 * code with no description is a gap in the catalogue, not a reason to stop
 * showing him the permission he holds.
 */
interface Meaning { module: string; description: string; movesMoney: boolean }
function useMeanings(): Map<string, Meaning> {
  const asked = useAsk(() => api.view('functions.list', {}), [], MESSAGES);
  return new Map((asked.data?.rows ?? []).map((r) => [r.function, { module: r.module, description: r.description, movesMoney: r.movesMoney }]));
}

/** One permission, named the way the office would name it, with its code underneath. */
function PermissionLabel({ fn, meaning }: { fn: string; meaning: Meaning | undefined }) {
  return (
    <span style={{ flex: 1 }}>
      <span title={fn}>{meaning?.description ?? fn}</span>
      {meaning?.movesMoney ? <Pill tone="testing">moves money or custody</Pill> : null}
    </span>
  );
}

/** Permissions in module order, so a manager reads one area of the business at a time. */
function byModule(fns: readonly string[], meanings: Map<string, Meaning>): { module: string; functions: string[] }[] {
  const groups = new Map<string, string[]>();
  for (const fn of fns) {
    const module = meanings.get(fn)?.module ?? 'other';
    groups.set(module, [...(groups.get(module) ?? []), fn]);
  }
  return [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0]))
    .map(([module, functions]) => ({ module, functions: functions.sort() }));
}

/**
 * WHAT THIS ROLE CAN DO, IN EIGHT LINES INSTEAD OF A HUNDRED AND TWENTY TICK BOXES.
 *
 * The owner: "120 types of permission is too much". The count cannot honestly come down —
 * every action carries its own permission so that letting somebody send a bill does not
 * also let them cancel a purchase order, and collapsing them would make the system less
 * safe rather than simpler. What CAN come down is how many a person has to READ. Roles are
 * the interface; permissions are the wiring. This says the areas and how much of each,
 * and the wiring waits under "Every permission" for the rare day somebody needs it.
 */
function WhatItCanDo({ held, meanings }: { held: ReadonlySet<string>; meanings: Map<string, Meaning> }) {
  const byArea = new Map<string, { n: number; money: number }>();
  for (const fn of held) {
    const m = meanings.get(fn);
    const area = words(m?.module ?? 'other');
    const cur = byArea.get(area) ?? { n: 0, money: 0 };
    byArea.set(area, { n: cur.n + 1, money: cur.money + (m?.movesMoney ? 1 : 0) });
  }
  const areas = [...byArea.entries()].sort((a, b) => b[1].n - a[1].n);
  if (areas.length === 0) return <p className="muted">This role grants nothing at all. Anybody holding it can sign in and will be refused everywhere.</p>;
  return (
    <>
      {areas.map(([area, v]) => (
        <div className="queue-item" key={area}>
          <span style={{ flex: 1 }}>
            <strong>{area}</strong>
            {v.money > 0
              ? <span className="muted"> · {count(v.money)} of these move money or custody</span>
              : null}
          </span>
          <Pill tone={v.money > 0 ? 'testing' : 'muted'}>{count(v.n)}</Pill>
        </div>
      ))}
    </>
  );
}

function OneRole({ roleId, session }: { roleId: number; session: Session }) {
  const asked = useAsk(() => api.view('roles.get', { roleId }), [roleId], MESSAGES);
  const users = useAsk(() => api.view('users.list', { active: true, pageSize: 200 }), [], MESSAGES);
  // read before the early returns below: a hook may not be called conditionally
  const meanings = useMeanings();
  const runner = useRun(MESSAGES);
  const [adding, setAdding] = useState<string[]>([]);
  const [removing, setRemoving] = useState<string[]>([]);
  const [reason, setReason] = useState('');
  // 14 Oct — the owner: "roles and permissions are to big lists". A hundred and
  // eighteen tick boxes, every one carrying its own code on a second line, is a
  // list nobody reads. A search, and one area open at a time.
  const [query, setQuery] = useState('');
  const [onlyGranted, setOnlyGranted] = useState(false);
  // Open is REMEMBERED. Applying a change reloads the role, and an uncontrolled <details>
  // would spring shut under the person mid-edit — which is also what the browser suite met.
  const [advanced, setAdvanced] = useState(false);

  if (asked.loading) return <Page title="Role"><Loading what="the role" /></Page>;
  if (asked.error) return <Page title="Role"><Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} /></Page>;
  const d = asked.data!;
  const may = can(session, 'role.manage');
  const held = new Set(d.functions);
  const canGrant = grantable(session);

  function toggleAdd(fn: string) { setAdding((o) => o.includes(fn) ? o.filter((x) => x !== fn) : [...o, fn]); }
  function toggleRemove(fn: string) { setRemoving((o) => o.includes(fn) ? o.filter((x) => x !== fn) : [...o, fn]); }

  async function apply() {
    let ok = true;
    if (adding.length) ok = await runner.run(() => api.command('role.grant', { roleId, functions: adding, reason: reason.trim() }));
    if (ok && removing.length) ok = await runner.run(() => api.command('role.revoke', { roleId, functions: removing, reason: reason.trim() }));
    if (ok) { setAdding([]); setRemoving([]); setReason(''); asked.reload(); }
  }

  const changing = adding.length > 0 || removing.length > 0;

  return (
    <Page title={`${d.role.code} — ${d.role.name}`}
          sub={`${count(d.role.functionCount)} permission(s) · held by ${count(d.role.holderCount)} person(s)`}>
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} {...(runner.error.reference ? { reference: runner.error.reference } : {})} /> : null}

      {changing ? (
        <Impact roleId={roleId} holders={d.holders} adding={adding} removing={removing} meanings={meanings} />
      ) : null}

      <div className="grid two">
        <Card title="What this role can do" sub={`${count(d.role.functionCount)} permissions across ${count(new Set([...held].map((f) => meanings.get(f)?.module ?? 'other')).size)} areas`}>
          <WhatItCanDo held={held} meanings={meanings} />
          <details style={{ marginTop: 12 }} open={advanced}
                   onToggle={(e: { currentTarget: { open: boolean } }) => setAdvanced(e.currentTarget.open)}>
            <summary style={{ cursor: 'pointer', fontWeight: 600, fontSize: 14, padding: '4px 0' }}>
              Every permission{may ? ', to change what it grants' : ''}
            </summary>
            <p className="muted" style={{ margin: '6px 0 10px' }}>
              {may
                ? 'Tick to add, untick to take away. Nothing is saved until you say why.'
                : 'Read only: you do not administer roles.'}
            </p>
          <div className="row" style={{ gap: 10, alignItems: 'center', marginBottom: 8 }}>
            <input type="search" placeholder="Search permissions" value={query}
                   onInput={(e: { currentTarget: { value: string } }) => setQuery(e.currentTarget.value)}
                   style={{ flex: 1 }} aria-label="Search permissions" />
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', textTransform: 'none', letterSpacing: 0, margin: 0, whiteSpace: 'nowrap' }}>
              <input type="checkbox" checked={onlyGranted} onChange={() => setOnlyGranted((v) => !v)} />
              only what it grants
            </label>
          </div>
          <div style={{ maxHeight: 460, overflowY: 'auto' }}>
            {byModule(canGrant, meanings)
              .map((group) => ({
                ...group,
                functions: group.functions.filter((fn) => {
                  if (onlyGranted && !held.has(fn)) return false;
                  const q = query.trim().toLowerCase();
                  if (!q) return true;
                  return fn.toLowerCase().includes(q)
                      || (meanings.get(fn)?.description ?? '').toLowerCase().includes(q)
                      || words(group.module).toLowerCase().includes(q);
                }),
              }))
              .filter((group) => group.functions.length > 0)
              .map((group) => {
                const granted = group.functions.filter((fn) => held.has(fn)).length;
                // An area opens when the search narrowed to it, or when this role
                // actually holds something in it. A hundred collapsed headings is a
                // list too; a hundred open ones is the wall the owner complained of.
                const open = query.trim().length > 0 || granted > 0;
                return (
              <details key={group.module} open={open}>
                <summary style={{ cursor: 'pointer', margin: '10px 0 2px', fontWeight: 600, fontSize: 14 }}>
                  {words(group.module)}
                  <span className="muted" style={{ fontWeight: 400 }}> · {granted} of {group.functions.length}</span>
                </summary>
                {group.functions.map((fn) => {
                  const has = held.has(fn);
                  const willAdd = adding.includes(fn);
                  const willRemove = removing.includes(fn);
                  const after = willAdd || (has && !willRemove);
                  return (
                    <div className="queue-item" key={fn}>
                      <label htmlFor={`fn-${fn}`} style={{ display: 'flex', gap: 10, alignItems: 'center', flex: 1, textTransform: 'none', letterSpacing: 0, margin: 0 }}>
                        <input id={`fn-${fn}`} type="checkbox" checked={after} disabled={!may}
                               onChange={() => (has ? toggleRemove(fn) : toggleAdd(fn))} />
                        <PermissionLabel fn={fn} meaning={meanings.get(fn)} />
                      </label>
                      {willAdd ? <Pill tone="good">adding</Pill> : null}
                      {willRemove ? <Pill tone="danger">taking away</Pill> : null}
                    </div>
                  );
                })}
              </details>
                );
              })}
          </div>
          {[...held].filter((f) => !canGrant.includes(f)).length ? (
            <Banner kind="info" title="This role grants permissions you do not hold yourself."
                    detail={`${[...held].filter((f) => !canGrant.includes(f)).map((f) => meanings.get(f)?.description ?? f).join('; ')} — they are shown nowhere above because you cannot give them away or take them back.`} />
          ) : null}
          {may ? (
            <>
              <TextArea name="change-reason" label="Why it is changing" value={reason} onChange={setReason}
                        help="kept with the change — a permission that appeared or vanished is always asked about later" />
              <Button kind="primary" wide onClick={() => { void apply(); }} disabled={!changing || !reason.trim() || runner.busy}>
                {runner.busy ? 'Saving…' : !changing ? 'Nothing has changed' : `Apply ${count(adding.length + removing.length)} change(s)`}
              </Button>
            </>
          ) : null}
          </details>
        </Card>

        <Card title="Who holds it" sub="every one of them gets every permission on the left">
          <Register
            columns={[
              { key: 'user', head: 'User', cell: (h: { username: string }) => <strong>{h.username}</strong> },
              { key: 'open', head: '', cell: (h: { userId: number }) => <Button small onClick={() => go(`#/users/${h.userId}`)}>What they may do</Button> },
            ]}
            rows={d.holders} empty="Nobody holds this role, so changing it affects no one today." />
          <p className="muted" style={{ marginBottom: 0 }}>
            {count(users.data?.page.total ?? 0)} user(s) in this company.
          </p>
        </Card>
      </div>
    </Page>
  );
}

/**
 * THE IMPACT, BEFORE THE CHANGE. The owner's rule (and plain sense): a manager
 * should see which people gain or lose a permission before he saves, not after
 * somebody telephones. Each holder is read individually, because the answer for
 * one may be "loses nothing — he has it directly as well".
 */
function Impact({ roleId, holders, adding, removing, meanings }: {
  roleId: number;
  holders: readonly { userId: number; username: string }[];
  adding: string[];
  removing: string[];
  meanings: Map<string, Meaning>;
}) {
  // the words AND the code: the words are what a manager understands, the code
  // is what he will be asked about afterwards, and a pill has room for both
  const name = (fn: string) => {
    const d = meanings.get(fn)?.description;
    return d ? `${d} (${fn})` : fn;
  };
  const asked = useAsk(
    () => Promise.all(holders.map((h) => api.view('users.get', { userId: h.userId }))),
    [roleId, holders.length, adding.join(','), removing.join(',')],
    MESSAGES,
  );
  if (asked.loading) return <Card title="What this would do"><Loading what="the people who hold it" /></Card>;
  if (asked.error) return <Card title="What this would do"><Banner kind="warn" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} /></Card>;

  const rows = (asked.data ?? []).map((u) => {
    const own = new Map(u.functions.map((f) => [f.function, f]));
    const gains = adding.filter((fn) => !own.has(fn));
    const loses = removing.filter((fn) => {
      const f = own.get(fn);
      if (!f) return false;
      // he keeps it if it is his own, or if another role also grants it
      return !f.direct && f.fromRoles.filter((r) => r !== undefined).length <= 1;
    });
    const keeps = removing.filter((fn) => own.has(fn) && !loses.includes(fn));
    return { username: u.user.username, gains, loses, keeps };
  });

  const anybodyAffected = rows.some((r) => r.gains.length || r.loses.length);

  return (
    <Card title="What this would do, before you do it" sub="who gains something, who loses something, and who is unaffected">
      {holders.length === 0 ? (
        <Banner kind="info" title="Nobody holds this role, so this change affects no one today." />
      ) : !anybodyAffected ? (
        <Banner kind="info" title="Nobody gains or loses anything." detail="Everybody holding this role already has these permissions by another route." />
      ) : (
        <Register
          columns={[
            { key: 'user', head: 'User', cell: (r: { username: string }) => <strong>{r.username}</strong> },
            { key: 'gains', head: 'Gains', cell: (r: { gains: string[] }) => r.gains.length ? <>{r.gains.map((f) => <Pill key={f} tone="good">{name(f)}</Pill>)}</> : <span className="muted">—</span> },
            { key: 'loses', head: 'Loses', cell: (r: { loses: string[] }) => r.loses.length ? <>{r.loses.map((f) => <Pill key={f} tone="danger">{name(f)}</Pill>)}</> : <span className="muted">—</span> },
            { key: 'keeps', head: 'Keeps anyway', cell: (r: { keeps: string[] }) => r.keeps.length ? <span className="muted">{r.keeps.map(name).join(', ')} — their own, or another role</span> : <span className="muted">—</span> },
          ]}
          rows={rows} empty="nobody" />
      )}
    </Card>
  );
}

function NewRole({ session }: { session: Session }) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [functions, setFunctions] = useState<string[]>([]);
  const runner = useRun(MESSAGES);
  const canGrant = grantable(session);
  const meanings = useMeanings();

  async function submit() {
    const ok = await runner.run(async () => {
      const r = await api.command('role.create', {
        code: code.trim(), name: name.trim(), functions,
        ...(description.trim() ? { description: description.trim() } : {}),
      });
      const value = r.value as { roleId?: number };
      if (value?.roleId) go(`#/roles/${value.roleId}`);
      return r;
    });
    if (!ok) return;
  }

  return (
    <Page title="New role" sub="A bundle of permissions with a name, so the same job is granted the same way twice.">
      <Card>
        {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
        <div className="grid two">
          <div>
            <Text name="code" label="Code" value={code} onChange={setCode} required autoFocus
                  help="short, and never changed once people hold it" />
            <Text name="name" label="Name" value={name} onChange={setName} required />
            <TextArea name="description" label="What this role is for" value={description} onChange={setDescription} />
          </div>
          <div>
            <div className="field"><label htmlFor="fn-list">Permissions</label></div>
            <div id="fn-list" style={{ maxHeight: 380, overflowY: 'auto' }}>
              {byModule(canGrant, meanings).map((group) => (
                <div key={group.module}>
                  <p className="muted" style={{ margin: '10px 0 2px' }}>{words(group.module)}</p>
                  {group.functions.map((fn) => (
                    <div className="queue-item" key={fn}>
                      <label htmlFor={`new-fn-${fn}`} style={{ display: 'flex', gap: 10, alignItems: 'center', flex: 1, textTransform: 'none', letterSpacing: 0, margin: 0 }}>
                        <input id={`new-fn-${fn}`} type="checkbox" checked={functions.includes(fn)}
                               onChange={() => setFunctions((o) => o.includes(fn) ? o.filter((x) => x !== fn) : [...o, fn])} />
                        <PermissionLabel fn={fn} meaning={meanings.get(fn)} />
                      </label>
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <p className="muted">You can only put permissions you hold yourself into a role.</p>
          </div>
        </div>
        <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !code.trim() || !name.trim() || functions.length === 0}>
          {runner.busy ? 'Creating…' : `Create the role with ${count(functions.length)} permission(s)`}
        </Button>
      </Card>
    </Page>
  );
}
